import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Canvas, painters, Outline } from 'headbreaker'
import Button from 'components/Button'
import {
	PuzzleContainer,
	PuzzleCanvasWrapper,
	PuzzleCanvas,
	SolvedImage,
	SolvedBanner,
	ButtonRow
} from './styles'
import { PartAdvancedModule } from 'components/CardDisplay/Parts';

const BASE_WIDTH = 500;
const BASE_HEIGHT = 600;
const MAX_VIEWPORT_HEIGHT_RATIO = 0.75;

type PuzzleProps = {
	src: string;
	title: string | null | undefined;
	success: string | null | undefined;
	legend: string | null | undefined;
	legendSize: string | null | undefined;
}

export default function Puzzle({ src, title = '', success = null, legend = '', legendSize = null }: PuzzleProps) {
	const puzzleRef = useRef<HTMLDivElement>(null);
	const backgroundRef = useRef<Canvas | null>(null);
	const [solved, setSolved] = useState(false);
	const { t } = useTranslation();

	const fitToScreen = () => {
		const puzzle = puzzleRef.current;
		const background = backgroundRef.current;
		if (!puzzle || !background) return ;
		const availableWidth = puzzle.parentElement?.clientWidth ?? window.innerWidth;
		const availableHeight = window.innerHeight * MAX_VIEWPORT_HEIGHT_RATIO;
		const scale = Math.min(availableWidth / BASE_WIDTH, availableHeight / BASE_HEIGHT);
		background.resize(BASE_WIDTH * scale, BASE_HEIGHT * scale);
		background.scale(scale);
		background.redraw();
	}

	useEffect(() => {
		const puzzle = puzzleRef.current;
		if (!puzzle) return ;
		let newPuzzle = new Image();
		newPuzzle.src = src;
		newPuzzle.onload = () => {
			console.log("loading image...");
			const background = new Canvas(puzzle.id || 'puzzle', {
				width: BASE_WIDTH, height: BASE_HEIGHT, pieceSize: 100, proximity: 20, borderFill: 10, strokeWidth: 2, lineSoftness: 0.12, image: newPuzzle, maxPiecesCount: { x: 5, y: 5}, painter: new painters.Konva(), outline: new Outline.Rounded()
			})

			backgroundRef.current = background;

			background.adjustImagesToPuzzleHeight();

			background.autogenerate({
				horizontalPieces: 5,
				verticalPieces: 5
			})
			background.attachSolvedValidator();
			background.onValid(() => setSolved(true));
			background.shuffle(0.7);
			background.draw();
			fitToScreen();
		}

		const resizeObserver = new ResizeObserver(fitToScreen);
		resizeObserver.observe(puzzle.parentElement ?? puzzle);
		window.addEventListener('resize', fitToScreen);

		return () => {
			newPuzzle.onload = null;
			resizeObserver.disconnect();
			window.removeEventListener('resize', fitToScreen);
		}
	}, [])

	const solvePuzzle = () => {
		const background = backgroundRef.current;
		if (!background) return ;
		background.solve();
		background.puzzle.validate();
		background.redraw();
	}

	const shufflePuzzle = () => {
		const background = backgroundRef.current;
		if (!background) return ;
		background.shuffle(0.7);
		background.puzzle.validate();
		setSolved(false);
		background.redraw();
	}

	return (
		<PuzzleContainer>
			{title && (
				<h2
				  style={{
					margin: '0 0 10px 10px',
					fontSize: '24px',
					fontWeight: '600',
					color: '#333',
					alignSelf: 'center',
				  }}
				>
		  			{title}
				</h2>
	  )}
			<PuzzleCanvasWrapper>
				<PuzzleCanvas ref={puzzleRef} id="puzzle" $hidden={solved} />
				{solved && <SolvedImage src={src} alt={title ?? ''} />}
				{solved && <SolvedBanner role="status">{success ? success : t('puzzle.solved')}</SolvedBanner>}
			</PuzzleCanvasWrapper>
			<ButtonRow>
				<Button onClick={solvePuzzle} >{t('puzzle.solve')}</Button>
				<Button onClick={shufflePuzzle} >{t('puzzle.shuffle')}</Button>
			</ButtonRow>
			{legend && legend.length > 0 && <legend style={{fontSize: legendSize ? legendSize === 'small' ? "13px" : "16px" : '13px'}}>{legend}</legend>}
		</PuzzleContainer>
	)
}